import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  CheckCircle2,
  ChevronRight,
  LogIn,
  PackageCheck,
  PackageX,
  Send,
  ShieldAlert,
  XCircle,
} from "lucide-react";
import {
  BOOKING_STATUSES,
  BOOKING_TRANSITIONS,
  STATUS_BADGE_CLASS,
} from "@/lib/constants";
import { useAuth } from "@/lib/auth";
import { transitionBooking } from "@/lib/api";

const ICONS = {
  pending_approval: Send,
  approved: CheckCircle2,
  rejected: XCircle,
  checked_in: LogIn,
  loading: PackageCheck,
  unloading: PackageX,
  completed: PackageCheck,
  cancelled: XCircle,
  no_show: ShieldAlert,
};

/**
 * Status banner + action buttons for a booking.
 *
 * Props:
 *   booking — { id, status, direction, client_id }
 *   onChanged(updatedRow) — fired after a successful transition
 *   inline — if true, render the actions compactly (used in row menus); default false
 */
export default function BookingStatusActions({ booking, onChanged, inline = false }) {
  const { user, hasRole } = useAuth();
  const [busy, setBusy] = useState(false);
  const [reasonFor, setReasonFor] = useState(null);
  const [reasonText, setReasonText] = useState("");

  // Local mirror of the booking so the banner refreshes immediately after a
  // transition — props don't change because the parent only bubbles the row
  // up (list refresh), it doesn't pass it back down into the open dialog.
  const [current, setCurrent] = useState(booking);
  // Only re-sync when a different booking row is opened (id change).
  // We intentionally exclude `booking` so optimistic local updates aren't clobbered.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setCurrent(booking); }, [booking?.id]);

  if (!current) return null;
  const meta = BOOKING_STATUSES.find((s) => s.value === current.status);
  const transitions = (BOOKING_TRANSITIONS[current.status] || []).filter((t) => {
    if (t.directions && !t.directions.includes(current.direction)) return false;
    if (t.roles && !hasRole(t.roles)) return false;
    return true;
  });

  const commit = async (transition, reason = null) => {
    setBusy(true);
    const { data, error } = await transitionBooking({
      bookingId: current.id,
      fromStatus: current.status,
      toStatus: transition.to,
      reason,
      currentUserId: user?.id,
      clientId: current.client_id,
    });
    setBusy(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    const newMeta = BOOKING_STATUSES.find((s) => s.value === transition.to);
    toast.success(`Status → ${newMeta?.label || transition.to}`);
    // Refresh the banner immediately + bubble for the parent list/grid.
    if (data) setCurrent(data);
    onChanged?.(data);
  };

  const handleClick = (transition) => {
    if (transition.requiresReason || transition.to === "cancelled") {
      setReasonText("");
      setReasonFor(transition);
      return;
    }
    commit(transition);
  };

  const handleReasonConfirm = async () => {
    if (!reasonText.trim() && reasonFor?.requiresReason) {
      toast.error("Please provide a reason");
      return;
    }
    const t = reasonFor;
    setReasonFor(null);
    await commit(t, reasonText.trim() || null);
  };

  return (
    <div
      data-testid="booking-status-actions"
      className={inline ? "" : "flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-slate-50 px-4 py-3"}
    >
      {!inline && (
        <div className="flex items-center gap-3">
          <span className="text-xs uppercase tracking-wider text-slate-500">Status</span>
          <Badge
            variant="secondary"
            className={STATUS_BADGE_CLASS[meta?.tone || "slate"]}
            data-testid="current-status-badge"
          >
            {meta?.label || booking.status}
          </Badge>
          {transitions.length === 0 && (
            <span className="text-xs text-slate-500">No further actions available.</span>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {transitions.map((t) => {
          const Icon = ICONS[t.to] || ChevronRight;
          // Phase 9.7c: block approve/confirm when Driver TBA is still on.
          const tbaBlock = booking?.driver_tba
                        && (t.to === "approved" || t.to === "confirmed");
          return (
            <Button
              key={t.to}
              size="sm"
              variant={t.destructive ? "outline" : "default"}
              className={t.destructive ? "border-rose-200 text-rose-700 hover:bg-rose-50" : ""}
              onClick={() => handleClick(t)}
              disabled={busy || tbaBlock}
              title={tbaBlock ? "Driver TBA — assign a real driver before approval" : undefined}
              data-testid={`status-action-${t.to}`}
            >
              <Icon className="h-4 w-4 mr-1.5" />
              {t.label}
            </Button>
          );
        })}
        {booking?.driver_tba && transitions.some((t) => t.to === "approved" || t.to === "confirmed") && (
          <span className="text-[11px] text-amber-700 italic ml-1"
                data-testid="approve-tba-block-hint">
            ⚠ Driver TBA — approval blocked until real driver assigned
          </span>
        )}
      </div>

      {/* Reason prompt */}
      <Dialog open={!!reasonFor} onOpenChange={(open) => !open && setReasonFor(null)}>
        <DialogContent data-testid="reason-dialog">
          <DialogHeader>
            <DialogTitle>{reasonFor?.label}</DialogTitle>
            <DialogDescription>
              {reasonFor?.to === "cancelled"
                ? "The booker and driver are emailed a cancellation notice with a link to book a new time. The reason is optional and shown in that email."
                : "This action is recorded against the booking and the audit log."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reason">Reason{reasonFor?.to === "cancelled" ? " (optional)" : ""}</Label>
            <Textarea
              id="reason"
              rows={3}
              value={reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              placeholder="Why are you doing this?"
              data-testid="reason-input"
            />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setReasonFor(null)} data-testid="reason-cancel-btn">
              Back
            </Button>
            <Button onClick={handleReasonConfirm} data-testid="reason-confirm-btn">
              {reasonFor?.to === "cancelled" ? "Cancel booking" : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
